/**
 * Supabase Auth: единственная точка импорта `config/supabase` и `@supabase/supabase-js`
 * для сценариев входа/выхода и подписки на сессию. React-слой подключается через этот модуль.
 */
import type { User as SupabaseUser } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';
import { ensureUserProfile, type UserProfile } from './dataClient';
import { requestWelcomeEmailIfNeeded } from './welcomeEmailClient';
import type { AuthUser, AuthProviderData } from '../types/AuthUser';

export async function signOutRobustly(): Promise<void> {
  const result = await Promise.race([
    supabase.auth.signOut(),
    new Promise<{ error: Error }>((resolve) => {
      setTimeout(() => {
        resolve({ error: new Error('signout_timeout') });
      }, 4000);
    }),
  ]);

  if (!result.error) {
    return;
  }

  const message = result.error.message?.toLowerCase() ?? '';
  const isNetworkLikeFailure =
    message.includes('failed to fetch') ||
    message.includes('network') ||
    message.includes('timeout') ||
    message.includes('abort');

  if (!isNetworkLikeFailure) {
    throw result.error;
  }

  const localFallback = await supabase.auth.signOut({ scope: 'local' });
  if (localFallback.error) {
    throw localFallback.error;
  }
}

export function normalizeAuthError(error: unknown): never {
  if (error instanceof TypeError && /failed to fetch/i.test(error.message)) {
    const networkError = new Error(
      'Нет соединения с Supabase Auth. Проверьте VITE_SUPABASE_URL/VITE_SUPABASE_ANON_KEY в окружении хостинга, доступ к интернету и блокировщики (AdBlock/VPN/корпоративный прокси).'
    ) as Error & { code?: string };
    networkError.code = 'network_error';
    throw networkError;
  }
  throw error;
}

async function syncUserProfileToDatabase(
  uid: string,
  profile: Partial<UserProfile>,
): Promise<void> {
  try {
    await ensureUserProfile(uid, profile);
  } catch (e) {
    console.error('Не удалось записать профиль пользователя в Supabase:', e);
  }
}

function trimMetaString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const t = value.trim();
  return t ? t : null;
}

function hasOAuthIdentity(user: SupabaseUser): boolean {
  for (const id of user.identities ?? []) {
    const p = id.provider?.trim();
    if (p && p !== 'email') return true;
  }
  return false;
}

/** Имя и фамилия из полей провайдера (given_name + family_name). */
function combinedGivenFamily(record: Record<string, unknown>): string | null {
  const given = trimMetaString(record.given_name);
  const family = trimMetaString(record.family_name);
  if (given && family) return `${given} ${family}`;
  return given ?? family ?? null;
}

/**
 * Имя для UI и timer_user_profiles.display_name.
 * При соцвходе приоритет — пара given_name + family_name (имя и фамилия), затем полное имя от провайдера.
 */
function displayNameFromSupabaseUser(user: SupabaseUser): string | null {
  const m = (user.user_metadata ?? {}) as Record<string, unknown>;

  if (hasOAuthIdentity(user)) {
    const metaPair = combinedGivenFamily(m);
    if (metaPair) return metaPair;

    for (const id of user.identities ?? []) {
      const p = id.provider?.trim();
      if (!p || p === 'email') continue;
      const d = id.identity_data as Record<string, unknown> | undefined;
      if (!d) continue;
      const pair = combinedGivenFamily(d);
      if (pair) return pair;
    }

    const metaFull =
      trimMetaString(m.full_name) ??
      trimMetaString(m.name) ??
      trimMetaString(m.displayName);
    if (metaFull) return metaFull;

    for (const id of user.identities ?? []) {
      const p = id.provider?.trim();
      if (!p || p === 'email') continue;
      const d = id.identity_data as Record<string, unknown> | undefined;
      if (!d) continue;
      const hit =
        trimMetaString(d.full_name) ??
        trimMetaString(d.name) ??
        trimMetaString(d.preferred_username);
      if (hit) return hit;
    }

    return null;
  }

  const metaPair = combinedGivenFamily(m);
  const fromMeta =
    trimMetaString(m.displayName) ??
    trimMetaString(m.full_name) ??
    trimMetaString(m.name) ??
    metaPair;
  if (fromMeta) return fromMeta;

  for (const id of user.identities ?? []) {
    const d = id.identity_data as Record<string, unknown> | undefined;
    if (!d) continue;
    const pair = combinedGivenFamily(d);
    const hit =
      trimMetaString(d.full_name) ??
      trimMetaString(d.name) ??
      trimMetaString(d.preferred_username) ??
      pair;
    if (hit) return hit;
  }

  return null;
}

function mapProviders(user: SupabaseUser): AuthProviderData[] {
  const providers = new Set<string>();
  for (const p of user.identities ?? []) {
    const provider = p.provider?.trim();
    if (provider) {
      if (provider === 'email') {
        providers.add('password');
      } else if (provider === 'google') {
        providers.add('google.com');
      } else {
        providers.add(provider);
      }
    }
  }
  if (!providers.size && user.email) {
    providers.add('password');
  }
  return Array.from(providers).map((providerId) => ({ providerId }));
}

function mapSupabaseUser(user: SupabaseUser): AuthUser {
  const legacyUidFromMetadata =
    typeof user.app_metadata?.firebase_uid === 'string' && user.app_metadata.firebase_uid.trim()
      ? user.app_metadata.firebase_uid.trim()
      : null;
  const displayName = displayNameFromSupabaseUser(user);
  const photoURL =
    (typeof user.user_metadata?.photoURL === 'string'
      ? user.user_metadata.photoURL
      : null) ??
    (typeof user.user_metadata?.avatar_url === 'string'
      ? user.user_metadata.avatar_url
      : null) ??
    null;

  return {
    uid: user.id,
    authUid: user.id,
    legacyUid: legacyUidFromMetadata,
    email: user.email ?? null,
    displayName,
    photoURL,
    providerData: mapProviders(user),
  };
}

async function syncUserAfterSignIn(sbUser: SupabaseUser): Promise<void> {
  /** Серверный user часто полнее session.user (имя Google и др. в user_metadata); без этого display_name в Postgres может не записаться. */
  const { data, error } = await supabase.auth.getUser();
  const user = !error && data.user ? data.user : sbUser;
  const mappedUser = mapSupabaseUser(user);
  await syncUserProfileToDatabase(mappedUser.uid, {
    email: user.email ?? null,
    displayName: mappedUser.displayName,
  });
  await requestWelcomeEmailIfNeeded();
}

export async function getInitialAuthUser(): Promise<AuthUser | null> {
  const { data } = await supabase.auth.getSession();
  const sbUser = data.session?.user ?? null;
  return sbUser ? mapSupabaseUser(sbUser) : null;
}

/** Подписка на изменения Auth; при SIGNED_IN / INITIAL_SESSION выполняется синхронизация профиля. */
export function subscribeSupabaseAuth(onUser: (user: AuthUser | null) => void): {
  unsubscribe: () => void;
} {
  const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
    const sbUser = session?.user ?? null;
    onUser(sbUser ? mapSupabaseUser(sbUser) : null);
    if (sbUser && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')) {
      void syncUserAfterSignIn(sbUser).catch((e) => {
        console.error('Supabase post-login sync error:', e);
      });
    }
  });

  return {
    unsubscribe: () => subscription.subscription.unsubscribe(),
  };
}

export async function signInWithGoogleOAuth(redirectTo: string): Promise<void> {
  try {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo },
    });
    if (error) throw error;
  } catch (error: unknown) {
    normalizeAuthError(error);
  }
}

export async function signInWithMagicLinkOtp(email: string, emailRedirectTo: string): Promise<void> {
  try {
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo },
    });
    if (error) throw error;
  } catch (error: unknown) {
    normalizeAuthError(error);
  }
}

export async function signInWithEmailPasswordCredentials(
  email: string,
  password: string,
): Promise<void> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (error) throw error;
    if (data.user) {
      await syncUserAfterSignIn(data.user);
    }
  } catch (error: unknown) {
    normalizeAuthError(error);
  }
}

export async function signUpWithEmailPasswordCredentials(
  email: string,
  password: string,
  emailRedirectTo: string,
): Promise<void> {
  try {
    const { data, error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: { emailRedirectTo },
    });
    if (error) throw error;
    if (data.user && data.session) {
      await syncUserAfterSignIn(data.user);
    }
  } catch (error: unknown) {
    normalizeAuthError(error);
  }
}
